import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const THEME_STORAGE_KEY = "marriage-platform-theme";

export const THEME_OPTIONS = [
  { value: "violet", label: "Soft Violet" },
  { value: "indigo", label: "Royal Indigo" },
  { value: "emerald", label: "Fresh Emerald" },
  { value: "amber", label: "Sunset Amber" },
  { value: "slate", label: "Cool Slate" },
];

const ThemeContext = createContext({
  theme: THEME_OPTIONS[0].value,
  setTheme: () => {},
});

const getInitialTheme = () => {
  if (typeof window === "undefined") return THEME_OPTIONS[0].value;
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  // Validate against the current option list — users upgrading from a
  // previous build may have a now-removed value (e.g. "teal", "rose")
  // saved. Without this guard the <select> would render with no
  // selection and the CSS would silently fall back to :root.
  const validValues = THEME_OPTIONS.map((o) => o.value);
  const initialTheme =
    stored && validValues.includes(stored) ? stored : THEME_OPTIONS[0].value;

  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", initialTheme);
  }

  return initialTheme;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    if (typeof document === "undefined") return;

    document.documentElement.setAttribute("data-theme", theme);
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme }), [theme]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

